import React, { useMemo, useState } from "react";
import "./InstallmentsCalculator.css";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Bookmark, Calculator, Download, Euro } from "lucide-react";
import {
  SimulatorHeader,
  ReuseCard,
  FieldWithIcon,
  FieldLabel,
} from "../components/SimulatorParts";
import useSimulatorFormat from "../components/useSimulatorFormat";
import { downloadSimulationWorkbook } from "../utils/simulationExcel";
import {
  asText,
  clampNumber,
  readSimulationWorkbook,
} from "../utils/simulationExcelImport";
import { BELGIUM_LOAN_PARAMS } from "../loan-simulator/LoanSimulatorBelgiumCalc";
import {
  MAX_TERM_YEARS,
  MIN_TERM_YEARS,
  calculateInstalmentBelgium,
} from "./InstallmentsCalculatorBelgiumCalc";

export default function InstallmentsCalculatorFlanders({ onBack, onContinue }) {
  const { t, i18n } = useTranslation("installments-calculator");
  const { money, percent } = useSimulatorFormat();

  const [loanAmount, setLoanAmount] = useState("");
  const [termYears, setTermYears] = useState(25);

  const result = useMemo(
    () => calculateInstalmentBelgium({ loanAmount, termYears }),
    [loanAmount, termYears]
  );

  const annualRate = Math.pow(1 + BELGIUM_LOAN_PARAMS.monthlyInterestRate, 12) - 1;

  const handleDownload = async () => {
    const currentInputs = {
      loanAmount,
      termYears,
    };

    const currentResult =
      calculateInstalmentBelgium(
        currentInputs,
      );

    if (!currentResult) {
      return;
    }

    await downloadSimulationWorkbook({
      simulationType: "MONTHLY_INSTALLMENT",
      region: "FLANDERS",
      language:
        i18n.resolvedLanguage ||
        i18n.language ||
        "en",
      calculatorVersion:
        BELGIUM_LOAN_PARAMS.version,
      inputs:
        currentInputs,
      results:
        currentResult,
    });
  };

  const handleApplyWorkbook = async (
    file,
  ) => {
    const { inputs } =
      await readSimulationWorkbook(
        file,
        {
          expectedSimulationType:
            "MONTHLY_INSTALLMENT",
          expectedRegion:
            "FLANDERS",
        },
      );

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "loanAmount",
      )
    ) {
      setLoanAmount(
        asText(
          inputs.loanAmount,
        ),
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(
        inputs,
        "termYears",
      )
    ) {
      setTermYears(
        clampNumber(
          inputs.termYears,
          MIN_TERM_YEARS,
          MAX_TERM_YEARS,
          25,
        ),
      );
    }
  };

  return (
    <div className="simulator">
      {/* ---------- LEFT COLUMN ---------- */}
      <div className="simulator__left-col">
        <SimulatorHeader eyebrow={t("eyebrow")} title={t("title")} subtitle={t("subtitle")} />

        <ReuseCard
          title={t("reuse-title")}
          description={t("reuse-desc")}
          browseLabel={t("browse")}
          applyLabel={t("upload-apply")}
          onApply={handleApplyWorkbook}
        />

        <div className="simulator__card simulator__form-card">
          <div className="simulator__section-header">
            <div className="simulator__section-title">
              <Calculator size={16} />
              <span>{t("your-loan")}</span>
            </div>
            <button type="button" className="simulator__btn-outline simulator__btn-outline--sm">
              <Bookmark size={14} /> {t("load-saved")}
            </button>
          </div>

          <div className="simulator__grid">
            <div className="simulator__field simulator__field--wide">
              <FieldLabel tip={t("tips.loan-amount-input")}>{t("loan-amount-input")}</FieldLabel>
              <FieldWithIcon icon={Euro} unit="€">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  placeholder="0"
                />
              </FieldWithIcon>
            </div>

            <div className="simulator__field simulator__field--slider simulator__field--wide">
              <FieldLabel tip={t("tips.term")}>{t("term")}</FieldLabel>
              <div className="simulator__slider-row">
                <input
                  type="range"
                  min={MIN_TERM_YEARS}
                  max={MAX_TERM_YEARS}
                  value={termYears}
                  onChange={(e) => setTermYears(Number(e.target.value))}
                />
                <span className="simulator__slider-value">{t("years", { count: termYears })}</span>
              </div>
              <div className="simulator__slider-limits">
                <span>{MIN_TERM_YEARS}</span>
                <span>{MAX_TERM_YEARS}</span>
              </div>
            </div>
          </div>

          <div className="simulator__footer">
            <button type="button" className="simulator__btn-back" onClick={onBack}>
              <ArrowLeft size={16} /> {t("back")}
            </button>
            <button type="button" className="simulator__btn-continue" onClick={onContinue}>
              {t("save-continue")} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ---------- RESULT ---------- */}
      <div className="simulator__card simulator__result-card">
        <div className="simulator__tags">
          <span className="simulator__tag">{t("tag-fixed")}</span>
          <span className="simulator__tag">{t("tag-annual-rate", { rate: percent(annualRate) })}</span>
          <span className="simulator__tag">{t("tag-term", { count: termYears })}</span>
          <span className="simulator__tag simulator__tag--badge">{t("tag-indicative")}</span>
        </div>

        <label className="simulator__result-label">{t("monthly-instalment")}</label>
        <span className="simulator__result-value">{result ? money(result.instalment) : "— €"}</span>

        {!result ? (
          <p className="simulator__empty-result">{t("empty-result")}</p>
        ) : (
          <>
            <div className="simulator__mini-cards">
              <div className="simulator__mini-card">
                <span className="simulator__mini-label">{t("total-repaid")}</span>
                <span className="simulator__mini-value">{money(result.totalRepaid)}</span>
              </div>
              <div className="simulator__mini-card">
                <span className="simulator__mini-label">{t("total-interest")}</span>
                <span className="simulator__mini-value">{money(result.totalInterest)}</span>
              </div>
            </div>

            <div className="simulator__rows">
              <div className="simulator__row">
                <span>{t("loan-amount")}</span>
                <span>{money(result.loanAmount)}</span>
              </div>
              <div className="simulator__row">
                <span>{t("loan-term")}</span>
                <span>{t("term-value", { years: result.termYears, months: result.termMonths })}</span>
              </div>
              <div className="simulator__row">
                <span>{t("monthly-rate")}</span>
                <span>{percent(result.monthlyInterestRate, 4)}</span>
              </div>
              <div className="simulator__row">
                <span>{t("annual-rate")}</span>
                <span>{percent(result.annualInterestRate)}</span>
              </div>
            </div>

            <p className="simulator__note">{t("costs-note")}</p>
          </>
        )}

        <p className="simulator__disclaimer">
          {t("disclaimer")}
          <br />
          {BELGIUM_LOAN_PARAMS.version}
        </p>

        <button
          type="button"
          className="simulator__btn-download"
          onClick={handleDownload}
          disabled={!result}
        >
          <Download size={16} /> {t("download")}
        </button>
      </div>
    </div>
  );
}
